import { isAxiosError } from 'axios';
import type { Collection, CollectionTag } from '@core/entities/Collection';
import type { Variable } from '@core/entities/Variable';
import type {
  CollectionRepository,
  CreateCollectionPayload,
  ExportedFile,
  ExportFormat,
  ImportFormat,
  UpdateCollectionPayload,
  VariableInput,
} from '@core/repositories/CollectionRepository';
import { HttpError } from '@core/errors/AppError';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';
import {
  toVariable,
  toVariableInputPayload,
  type VariableResponse,
} from '@infrastructure/mappers/toVariable';

interface CollectionResponse {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  color: string | null;
  position: number;
  created_by?: { id: string; name: string } | null;
  folder_count: number | null;
  request_count: number | null;
  is_favorited: boolean;
  tags: Array<{ id: string; name: string; slug: string; color: string | null }>;
  abilities: { view: boolean; update: boolean; delete: boolean };
  created_at: string;
  updated_at: string;
}

const toTag = (payload: CollectionResponse['tags'][number]): CollectionTag => ({
  id: payload.id,
  name: payload.name,
  slug: payload.slug,
  color: payload.color,
});

const toCollection = (payload: CollectionResponse): Collection => ({
  id: payload.id,
  workspaceId: payload.workspace_id,
  name: payload.name,
  description: payload.description,
  color: payload.color,
  position: payload.position,
  createdBy: payload.created_by ?? null,
  folderCount: payload.folder_count,
  requestCount: payload.request_count,
  isFavorited: payload.is_favorited,
  tags: (payload.tags ?? []).map(toTag),
  abilities: payload.abilities,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

export class CollectionRepositoryImpl implements CollectionRepository {
  async list(workspaceId: string): Promise<Collection[]> {
    try {
      const response = await httpClient.get<{ data: CollectionResponse[] }>(
        `/workspaces/${workspaceId}/collections`,
      );
      return response.data.data.map(toCollection);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<Collection> {
    try {
      const response = await httpClient.get<{ data: CollectionResponse }>(`/collections/${id}`);
      return toCollection(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(workspaceId: string, payload: CreateCollectionPayload): Promise<Collection> {
    try {
      const response = await httpClient.post<{ data: CollectionResponse }>(
        `/workspaces/${workspaceId}/collections`,
        payload,
      );
      return toCollection(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: UpdateCollectionPayload): Promise<Collection> {
    try {
      const response = await httpClient.put<{ data: CollectionResponse }>(
        `/collections/${id}`,
        payload,
      );
      return toCollection(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async duplicate(id: string): Promise<Collection> {
    try {
      const response = await httpClient.post<{ data: CollectionResponse }>(
        `/collections/${id}/duplicate`,
      );
      return toCollection(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async reorder(workspaceId: string, orderedIds: string[]): Promise<void> {
    try {
      await httpClient.post(`/workspaces/${workspaceId}/collections/reorder`, { ids: orderedIds });
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/collections/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async favorite(id: string): Promise<void> {
    try {
      await httpClient.post(`/collections/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async unfavorite(id: string): Promise<void> {
    try {
      await httpClient.delete(`/collections/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async import(workspaceId: string, format: ImportFormat, payload: string): Promise<Collection> {
    try {
      const response = await httpClient.post<{ data: CollectionResponse }>(
        `/workspaces/${workspaceId}/collections/import`,
        { format, payload },
      );
      return toCollection(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async exportCollection(id: string, format: ExportFormat): Promise<ExportedFile> {
    try {
      const response = await httpClient.get<Blob>(`/collections/${id}/export`, {
        params: { format },
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'] as string | undefined;
      const match = disposition?.match(/filename="([^"]+)"/);
      const fallback = format === 'curl' ? 'collection.sh' : 'collection.postman_collection.json';
      return { blob: response.data, filename: match?.[1] ?? fallback };
    } catch (error) {
      // With responseType: 'blob', an error response body also arrives as a
      // Blob (axios applies responseType regardless of status), so the
      // generic mapper can't read `.message` off it — decode it here first.
      if (isAxiosError(error) && error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        let message = error.message;
        try {
          message = (JSON.parse(text) as { message?: string }).message ?? message;
        } catch {
          // body wasn't JSON — keep the generic axios message
        }
        throw new HttpError(message, error.response.status);
      }
      throw toAppError(error);
    }
  }

  async listVariables(id: string): Promise<Variable[]> {
    try {
      const response = await httpClient.get<{ data: VariableResponse[] }>(
        `/collections/${id}/variables`,
      );
      return response.data.data.map(toVariable);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async syncVariables(id: string, variables: VariableInput[]): Promise<Variable[]> {
    try {
      const response = await httpClient.put<{ data: VariableResponse[] }>(
        `/collections/${id}/variables`,
        {
          variables: variables.map(toVariableInputPayload),
        },
      );
      return response.data.data.map(toVariable);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
